import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem as MuiMenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { AppIconButton } from '../ui';
import { inventoryApi, PrepIngredientPayload } from '../../api/inventoryApi';
import { formatQuantity } from '../../utils/formatQuantity';
import type { ThemeColors } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';

const UNIT_LABELS: Record<string, string> = {
  KG: 'KG',
  GRAMS: 'g',
  LITRE: 'L',
  ML: 'ml',
  PIECES: 'pcs',
  UNITS: 'units',
};

const unitLabel = (unit: string) => UNIT_LABELS[unit] || unit;

interface PrepRecipeIngredient {
  ingredientItemId: number;
  name: string;
  unit: string;
  quantityPerUnit?: number | null;
  batchQuantity?: number | null;
  batchYield?: number | null;
  currentStock?: number;
}

interface Shortfall {
  name: string;
  unit: string;
  needed: number;
  deducted: number;
}

interface ProduceResult {
  quantityProduced: number;
  newStock: number;
  shortfalls: Shortfall[];
}

/** A single editable ingredient row: how much of this ingredient goes into one batch. */
interface EditRow {
  ingredientItemId: number | '';
  amountPerBatch: number;
}

interface ApiError {
  response?: { data?: { error?: string; message?: string } };
}

interface PrepRecipeDialogProps {
  open: boolean;
  onClose: () => void;
  /** the prepared (kitchen-made) inventory item this recipe belongs to */
  item: { id: number; name: string; unit: string } | null;
  /** all inventory items, used to pick ingredients */
  inventoryItems: { id: number; name: string; unit: string; currentStock: number }[];
  theme: ThemeColors;
  /** called after a successful save or production so the parent can refresh stock */
  onChanged?: () => void;
}

/**
 * Manage the recipe for a kitchen-prepared inventory item (e.g. "idli batter",
 * "sambar base") and record production runs. Recipes are entered per batch —
 * "one batch makes X {unit}" plus the ingredient amounts that go into it.
 */
const PrepRecipeDialog: React.FC<PrepRecipeDialogProps> = ({
  open,
  onClose,
  item,
  inventoryItems,
  theme,
  onChanged,
}) => {
  const confirm = useConfirm();
  const [batchYield, setBatchYield] = useState<number>(1);
  const [rows, setRows] = useState<EditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [produceQty, setProduceQty] = useState<number>(0);
  const [produceNotes, setProduceNotes] = useState('');
  const [producing, setProducing] = useState(false);
  const [produceResult, setProduceResult] = useState<ProduceResult | null>(null);

  // Ingredients can be any inventory item except the prepared item itself
  const ingredientOptions = inventoryItems.filter((i) => i.id !== item?.id);

  const loadRecipe = useCallback(async () => {
    if (!item) return;
    setLoading(true);
    setError(null);
    try {
      const res = await inventoryApi.getPrepRecipe(item.id);
      const ingredients: PrepRecipeIngredient[] = res.data?.ingredients || [];
      // A recipe is either batch-mode (batchYield present) or a plain per-unit
      // amount. Derive one shared "batch makes X" from the first batch row.
      const withYield = ingredients.find((ing) => ing.batchYield != null && ing.batchYield > 0);
      setBatchYield(withYield?.batchYield ?? 1);
      setRows(
        ingredients.map((ing) => ({
          ingredientItemId: ing.ingredientItemId,
          amountPerBatch: ing.batchQuantity ?? ing.quantityPerUnit ?? 0,
        }))
      );
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to load recipe');
      setBatchYield(1);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [item]);

  useEffect(() => {
    if (open && item) {
      // reset transient production state whenever the dialog (re)opens
      setProduceQty(0);
      setProduceNotes('');
      setProduceResult(null);
      loadRecipe();
    }
  }, [open, item, loadRecipe]);

  const handleAddRow = () => {
    setRows((prev) => [...prev, { ingredientItemId: '', amountPerBatch: 0 }]);
  };

  const handleRemoveRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, patch: Partial<EditRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleSaveRecipe = async () => {
    if (!item) return;
    const validRows = rows.filter((r) => r.ingredientItemId !== '' && r.amountPerBatch > 0);

    if (validRows.length === 0) {
      const ok = await confirm({
        title: 'Remove recipe',
        description: 'Remove recipe? Item will no longer be marked as kitchen-prepared.',
        confirmText: 'Remove',
        cancelText: 'Cancel',
      });
      if (!ok) return;
    } else if (batchYield <= 0) {
      setError('One batch must make a quantity greater than zero.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const ingredients: PrepIngredientPayload[] = validRows.map((r) => ({
        ingredientItemId: r.ingredientItemId as number,
        batchQuantity: r.amountPerBatch,
        batchYield,
      }));
      await inventoryApi.setPrepRecipe(item.id, { ingredients });
      onChanged?.();
      if (ingredients.length === 0) {
        onClose();
      }
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to save recipe');
    } finally {
      setSaving(false);
    }
  };

  const handleProduce = async () => {
    if (!item || produceQty <= 0) return;
    setProducing(true);
    setError(null);
    setProduceResult(null);
    try {
      const res = await inventoryApi.producePrep(item.id, {
        quantity: produceQty,
        notes: produceNotes.trim() || undefined,
      });
      setProduceResult({
        quantityProduced: res.data?.quantityProduced ?? produceQty,
        newStock: res.data?.newStock ?? 0,
        shortfalls: res.data?.shortfalls || [],
      });
      setProduceQty(0);
      setProduceNotes('');
      onChanged?.();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to record production');
    } finally {
      setProducing(false);
    }
  };

  const outlineButtonSx = {
    backgroundColor: `${theme.button?.secondary?.bg || theme.background} !important`,
    color: `${theme.primary} !important`,
    border: `1px solid ${theme.primary} !important`,
    fontWeight: 600,
    textTransform: 'none' as const,
    boxShadow: 'none !important',
    '&:disabled': { opacity: 0.5 },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.components?.card?.background,
          border: `1px solid ${theme.components?.card?.border}`,
        },
      }}
    >
      <DialogTitle sx={{ color: theme.text, fontWeight: 700 }}>
        {item ? `Recipe: ${item.name}` : 'Recipe'}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {/* Section 1: recipe per batch */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text, mb: 0.5 }}>
              Recipe (per batch)
            </Typography>
            <Typography variant="body2" sx={{ color: theme.gray?.[600], mb: 2 }}>
              List the ingredients that go into one batch, then how much that batch makes.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: theme.text }}>
                One batch makes
              </Typography>
              <TextField
                type="number"
                size="small"
                value={batchYield || ''}
                onChange={(e) => setBatchYield(parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 'any', 'data-testid': 'prep-batch-yield', style: { width: 80 } }}
              />
              <Typography variant="body2" sx={{ color: theme.text }}>
                {item ? unitLabel(item.unit) : ''}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {rows.map((row, index) => {
                const selected = ingredientOptions.find((i) => i.id === row.ingredientItemId);
                return (
                  <Box
                    key={index}
                    data-testid="prep-ingredient-row"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                      <InputLabel>Ingredient</InputLabel>
                      <Select
                        value={row.ingredientItemId}
                        label="Ingredient"
                        onChange={(e) => updateRow(index, { ingredientItemId: Number(e.target.value) })}
                        inputProps={{ 'data-testid': 'prep-ingredient-select' }}
                      >
                        {ingredientOptions.map((opt) => (
                          <MuiMenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MuiMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      type="number"
                      size="small"
                      label={`Amount${selected ? ` (${unitLabel(selected.unit)})` : ''}`}
                      value={row.amountPerBatch || ''}
                      onChange={(e) => updateRow(index, { amountPerBatch: parseFloat(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: 'any', 'data-testid': 'prep-ingredient-qty' }}
                      sx={{ width: 130 }}
                    />
                    <AppIconButton
                      variant="danger"
                      tooltip="Remove"
                      size="small"
                      icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                      onClick={() => handleRemoveRow(index)}
                    />
                  </Box>
                );
              })}
            </Box>

            <Button
              startIcon={<PlusIcon className="h-4 w-4" />}
              onClick={handleAddRow}
              data-testid="prep-add-ingredient"
              size="small"
              sx={{ mt: 1.5, color: theme.primary, textTransform: 'none', fontWeight: 600 }}
            >
              Add ingredient
            </Button>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSaveRecipe}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : undefined}
                data-testid="prep-save-recipe"
                sx={outlineButtonSx}
              >
                Save recipe
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Section 2: record production */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text, mb: 0.5 }}>
              Record production
            </Typography>
            <Typography variant="body2" sx={{ color: theme.gray?.[600], mb: 2 }}>
              Made a fresh batch? Record how much you produced — the ingredients are deducted automatically.
            </Typography>

            {produceResult && (
              <>
                <Alert severity="success" sx={{ mb: 2 }} data-testid="prep-produce-success">
                  Added {item ? formatQuantity(produceResult.quantityProduced, item.unit) : produceResult.quantityProduced}.
                  {' '}New stock: {item ? formatQuantity(produceResult.newStock, item.unit) : produceResult.newStock}.
                </Alert>
                {produceResult.shortfalls.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }} data-testid="prep-produce-shortfalls">
                    Some ingredients ran short:
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {produceResult.shortfalls.map((s, i) => (
                        <li key={i}>
                          {s.name}: needed {formatQuantity(s.needed, s.unit)}, only {formatQuantity(s.deducted, s.unit)} available
                        </li>
                      ))}
                    </Box>
                  </Alert>
                )}
              </>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: theme.text }}>
                We made
              </Typography>
              <TextField
                type="number"
                size="small"
                value={produceQty || ''}
                onChange={(e) => setProduceQty(parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 'any', 'data-testid': 'prep-produce-qty', style: { width: 80 } }}
              />
              <Typography variant="body2" sx={{ color: theme.text }}>
                {item ? unitLabel(item.unit) : ''}
              </Typography>
            </Box>
            <TextField
              label="Notes (optional)"
              size="small"
              fullWidth
              value={produceNotes}
              onChange={(e) => setProduceNotes(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleProduce}
                disabled={producing || produceQty <= 0}
                startIcon={producing ? <CircularProgress size={16} /> : undefined}
                data-testid="prep-produce-btn"
                sx={{
                  backgroundColor: `${theme.accent}22 !important`,
                  color: `${theme.accent} !important`,
                  border: `1px solid ${theme.accent} !important`,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none !important',
                  '&:hover': { backgroundColor: `${theme.accent}44 !important`, boxShadow: 'none !important' },
                  '&:disabled': { opacity: 0.5 },
                }}
              >
                Record
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: theme.gray?.[600], textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrepRecipeDialog;
